import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, MessageSquare, Users, ScrollText } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, ErrorState, EmptyState } from "@/components/Bits";
import { Modal, Field, inputCls, primaryBtnCls, ghostBtnCls, Table, Td } from "@/components/Kit";
import { NoAccess } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { dmyTime } from "@/lib/format";

const STATUS_TONE = { sent: "text-emerald-600", pending: "text-amber-600", skipped: "text-slate-400", failed: "text-red-600" };

export default function WhatsAppManager() {
  const { can } = useAuth();
  const [params, setParams] = useSearchParams();
  const modules = ["hospital", "lab", "pharmacy"].filter((m) => can(`${m}.whatsapp.manage`));
  const module = params.get("module") || modules[0] || "";
  const manage = modules.includes(module);
  const [tab, setTab] = useState("templates");
  const [data, setData] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [logs, setLogs] = useState([]);
  const [error, setError] = useState("");
  const [tplOpen, setTplOpen] = useState(false);
  const [tpl, setTpl] = useState(null);
  const [ctOpen, setCtOpen] = useState(false);
  const [ct, setCt] = useState({ name: "", number: "", role: "admin", purpose: "", active: true });

  const load = useCallback(async () => {
    if (!manage) return;
    setError(""); setData(null);
    try {
      const [t, c, l] = await Promise.all([
        api.get("/settings/whatsapp/templates", { params: { module } }), api.get("/settings/whatsapp/contacts", { params: { module } }),
        api.get("/settings/whatsapp/logs", { params: { module } })]);
      setData(t.data); setContacts(c.data.items); setLogs(l.data.items);
    } catch (e) { setError(apiError(e)); }
  }, [module, manage]);
  useEffect(() => { load(); }, [load]);

  const openTpl = (t) => setTpl(t ? { ...t, variables_text: (t.variables || []).map((v) => v.name).join(", ") }
    : { name: "", interakt_template: "", module, notification_type: "custom", language: "en", active: true,
        recipient_type: "patient", variables_text: "", applicable_business: "all", trigger_event: "" });
  useEffect(() => { setTplOpen(Boolean(tpl)); }, [tpl]);

  const saveTpl = async (e) => {
    e.preventDefault();
    const payload = { ...tpl, variables: (tpl.variables_text || "").split(",").map((s) => s.trim()).filter(Boolean).map((n) => ({ name: n, source: n })) };
    delete payload.variables_text;
    try {
      if (tpl.template_id) await api.put(`/settings/whatsapp/templates/${tpl.template_id}`, payload);
      else await api.post("/settings/whatsapp/templates", payload);
      toast.success("Template saved."); setTpl(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveCt = async (e) => {
    e.preventDefault();
    try { await api.post("/settings/whatsapp/contacts", { ...ct, module }); toast.success("Contact added."); setCtOpen(false); setCt({ name: "", number: "", role: "admin", purpose: "", active: true }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!manage) return <NoAccess what="WhatsApp settings for this module" />;
  const tabCls = (on) => `inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium border ${on ? "bg-[#0F172A] text-white border-[#0F172A]" : "border-slate-300 text-slate-700 hover:border-slate-900"}`;

  return (
    <div data-testid="whatsapp-manager-page">
      <PageHeader overline="Settings" title="WhatsApp Template Manager"
        subtitle="Configure reusable Interakt notification templates, variable mappings, contacts and view the delivery log. Interakt API credentials remain securely backend-only." />

      <label className="block mb-5 text-sm font-medium">Module
        <select data-testid="wa-module-select" className={`${inputCls} mt-2 max-w-xs`} value={module}
          onChange={(e) => { setParams({ module: e.target.value }); setTpl(null); setCtOpen(false); }}>
          {modules.map((m) => <option key={m} value={m}>{m === "lab" ? "Laboratory" : m === "hospital" ? "Hospital" : "Pharmacy"}</option>)}
        </select>
      </label>
      {data?.config && (
        <div className="panel p-4 mb-5 text-sm" data-testid="wa-config-status">
          Interakt status: <span className={data.config.configured ? "text-emerald-600 font-semibold" : "text-amber-600 font-semibold"}>
            {data.config.configured ? "Connected" : "Not configured in this environment"}</span>
          <span className="text-slate-400"> · credentials are never shown here</span>
        </div>
      )}

      <div className="flex flex-wrap gap-3 mb-6">
        <button onClick={() => setTab("templates")} className={tabCls(tab === "templates")} data-testid="wa-tab-templates"><MessageSquare className="h-4 w-4" /> Templates</button>
        <button onClick={() => setTab("contacts")} className={tabCls(tab === "contacts")} data-testid="wa-tab-contacts"><Users className="h-4 w-4" /> Contacts</button>
        <button onClick={() => setTab("logs")} className={tabCls(tab === "logs")} data-testid="wa-tab-logs"><ScrollText className="h-4 w-4" /> Delivery log</button>
      </div>

      {error && <ErrorState message={error} />}
      {!data ? <Loading /> : tab === "templates" ? (
        <>
          {manage && <div className="mb-4"><button onClick={() => openTpl(null)} className={primaryBtnCls} data-testid="add-template-btn"><Plus className="h-4 w-4" /> New template</button></div>}
          <Table testid="wa-templates-table" columns={["Name", "Notification", "Interakt template", "Recipient", "Variables", "Active", manage ? "" : ""]}
            rows={data.items} empty={<EmptyState title="No templates" testid="wa-templates-empty" />}
            renderRow={(t) => (
              <tr key={t.template_id} data-testid={`wa-template-${t.template_id}`}>
                <Td className="font-medium text-slate-900">{t.name}</Td>
                <Td className="text-slate-600">{t.notification_type}</Td>
                <Td className="text-slate-600">{t.interakt_template || <span className="text-amber-600">not set</span>}</Td>
                <Td className="text-slate-600">{t.recipient_type}</Td>
                <Td className="text-slate-500 text-xs">{(t.variables || []).map((v) => v.name).join(", ")}</Td>
                <Td><span className={`text-xs font-medium ${t.active ? "text-emerald-600" : "text-slate-400"}`}>{t.active ? "Active" : "Inactive"}</span></Td>
                {manage && <Td><button onClick={() => openTpl(t)} className={ghostBtnCls} data-testid={`edit-template-${t.template_id}`}>Edit</button></Td>}
              </tr>
            )} />
        </>
      ) : tab === "contacts" ? (
        <>
          {manage && <div className="mb-4"><button onClick={() => setCtOpen(true)} className={primaryBtnCls} data-testid="add-contact-btn"><Plus className="h-4 w-4" /> Add contact</button></div>}
          <Table testid="wa-contacts-table" columns={["Name", "Number", "Role", "Purpose", "Active"]}
            rows={contacts} empty={<EmptyState title="No contacts" subtitle="Add owner / admin / finance / lab WhatsApp contacts." testid="wa-contacts-empty" />}
            renderRow={(c) => (
              <tr key={c.contact_id} data-testid={`wa-contact-${c.contact_id}`}>
                <Td className="font-medium text-slate-900">{c.name}</Td><Td>{c.number}</Td>
                <Td className="capitalize text-slate-600">{c.role}</Td><Td className="text-slate-500">{c.purpose || "—"}</Td>
                <Td><span className={`text-xs font-medium ${c.active ? "text-emerald-600" : "text-slate-400"}`}>{c.active ? "Active" : "Inactive"}</span></Td>
              </tr>
            )} />
        </>
      ) : (
        <Table testid="wa-logs-table" columns={["Time", "Template", "Recipient", "Ref", "Status", "Detail"]}
          rows={logs} empty={<EmptyState title="No delivery attempts yet" testid="wa-logs-empty" />}
          renderRow={(l) => (
            <tr key={l.log_id} data-testid={`wa-log-${l.log_id}`}>
              <Td className="text-xs text-slate-500">{dmyTime(l.created_at)}</Td>
              <Td className="text-slate-800">{l.template_name}</Td>
              <Td className="text-slate-600">{l.recipient_label} <span className="text-xs text-slate-400">···{l.phone_last4}</span></Td>
              <Td className="text-xs text-slate-500">{l.ref_type}{l.ref_id ? `:${l.ref_id}` : ""}</Td>
              <Td><span className={`text-xs font-semibold capitalize ${STATUS_TONE[l.status] || "text-slate-500"}`}>{l.status}</span></Td>
              <Td className="text-xs text-slate-400">{l.error || "—"}</Td>
            </tr>
          )} />
      )}

      <Modal open={tplOpen} onClose={() => setTpl(null)} wide title={tpl?.template_id ? "Edit template" : "New template"} testid="wa-template-modal">
        {tpl && (
          <form onSubmit={saveTpl} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Template name *"><input className={inputCls} data-testid="tpl-name" value={tpl.name} onChange={(e) => setTpl({ ...tpl, name: e.target.value })} /></Field>
              <Field label="Interakt template identifier"><input className={inputCls} data-testid="tpl-interakt" value={tpl.interakt_template} onChange={(e) => setTpl({ ...tpl, interakt_template: e.target.value })} placeholder="exact approved name in Interakt" /></Field>
              <Field label="Notification type *">
                <select className={inputCls} data-testid="tpl-type" value={tpl.notification_type} onChange={(e) => setTpl({ ...tpl, notification_type: e.target.value })}>
                  {data.notification_types.map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </Field>
              <Field label="Recipient type *">
                <select className={inputCls} data-testid="tpl-recipient" value={tpl.recipient_type} onChange={(e) => setTpl({ ...tpl, recipient_type: e.target.value })}>
                  {data.recipient_types.map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </Field>
              <Field label="Language"><input className={inputCls} data-testid="tpl-lang" value={tpl.language} onChange={(e) => setTpl({ ...tpl, language: e.target.value })} /></Field>
              <Field label="Applicable business/facility"><input className={inputCls} value={tpl.applicable_business} onChange={(e) => setTpl({ ...tpl, applicable_business: e.target.value })} /></Field>
              <Field label="Trigger / event"><input className={inputCls} data-testid="tpl-event" value={tpl.trigger_event} onChange={(e) => setTpl({ ...tpl, trigger_event: e.target.value })} placeholder="defaults to notification type" /></Field>
              <Field label="Active">
                <select className={inputCls} data-testid="tpl-active" value={tpl.active ? "1" : "0"} onChange={(e) => setTpl({ ...tpl, active: e.target.value === "1" })}>
                  <option value="1">Active</option><option value="0">Inactive</option>
                </select>
              </Field>
            </div>
            <Field label="Variables & data mapping (ordered, comma-separated — must match the Interakt body order)">
              <input className={inputCls} data-testid="tpl-variables" value={tpl.variables_text} onChange={(e) => setTpl({ ...tpl, variables_text: e.target.value })} placeholder="Ordered variable names for the selected module" />
            </Field>
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setTpl(null)} className={ghostBtnCls}>Cancel</button>
              <button className={primaryBtnCls} data-testid="save-template-btn">Save template</button>
            </div>
          </form>
        )}
      </Modal>

      <Modal open={ctOpen} onClose={() => setCtOpen(false)} title="Add WhatsApp contact" testid="wa-contact-modal">
        <form onSubmit={saveCt} className="space-y-4">
          <Field label="Name *"><input className={inputCls} data-testid="ct-name" value={ct.name} onChange={(e) => setCt({ ...ct, name: e.target.value })} /></Field>
          <Field label="Number *"><input className={inputCls} data-testid="ct-number" value={ct.number} onChange={(e) => setCt({ ...ct, number: e.target.value })} /></Field>
          <Field label="Role / purpose">
            <select className={inputCls} data-testid="ct-role" value={ct.role} onChange={(e) => setCt({ ...ct, role: e.target.value })}>
              {["owner", "admin", "finance", "lab", "other"].map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </Field>
          <div className="flex justify-end"><button className={primaryBtnCls} data-testid="save-contact-btn">Save contact</button></div>
        </form>
      </Modal>
    </div>
  );
}
